import { runtimeConfig } from '@/config/runtime-config'
import { dispatchRuntimeChatStream } from '@/features/shared/chat/stream/stream'
import { getDeviceTimezone } from '@/features/shared/timezone/timezone'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

import type {
  ChatMessageFeedbackRating,
  RuntimeActiveRunDto,
  RuntimeConversationDto,
  RuntimeConversationListDto,
  RuntimeMessageDto,
  RuntimeSseEvent,
  RuntimeStreamIdentity,
  RuntimeToolInvocationDetailDto,
} from '@/features/shared/chat/types'

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface ListCoreAgentConversationsOptions {
  lastId?: string
  limit?: number
}

const CONVERSATION_MESSAGE_LIMIT = 100

export interface CoreAgentModelOption {
  id: string
  interface_type?: string
  label: string
  model_name?: string
  provider?: string
  public_model_name?: string
}

export interface CoreAgentViewConfig {
  default_model_id: string
  enabled: boolean
  models: CoreAgentModelOption[]
  show_tool_invocation_details?: boolean
  opening: {
    starter_questions?: string[]
    statement?: string | null
  }
}

export interface CoreAgentStatus {
  enabled: boolean
}

export const coreAgentStatusQueryKey = ['core-agent-status'] as const

export interface CoreAgentSendOptions {
  conversationId?: string
  onStreamIdentity?: (identity: RuntimeStreamIdentity) => void
  query: string
  registeredModelId?: string
  signal?: AbortSignal
  submissionId: string
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('user'),
  portal: 'user',
})

export async function getCoreAgentViewConfig() {
  const response = await apiClient.request<SuccessEnvelope<CoreAgentViewConfig>>(
    '/app/core-agent/view-config',
  )

  return response.data
}

export async function getCoreAgentStatus() {
  const response =
    await apiClient.request<SuccessEnvelope<CoreAgentStatus>>('/app/core-agent/status')

  return response.data
}

export async function listCoreAgentConversations(options: ListCoreAgentConversationsOptions = {}) {
  const query = new URLSearchParams({
    limit: String(options.limit ?? 30),
  })

  if (options.lastId) {
    query.set('last_id', options.lastId)
  }

  const response = await apiClient.request<SuccessEnvelope<RuntimeConversationListDto>>(
    `/app/core-agent/conversations?${query.toString()}`,
  )

  return response.data
}

export async function getCoreAgentConversationMessages(conversationId: string) {
  const response = await apiClient.request<
    SuccessEnvelope<{ data: RuntimeMessageDto[]; has_more: boolean }>
  >(
    `/app/core-agent/conversations/${encodeURIComponent(conversationId)}/messages?limit=${String(CONVERSATION_MESSAGE_LIMIT)}`,
  )

  return response.data
}

export async function getCoreAgentToolInvocationDetail(
  conversationId: string,
  invocationId: string,
) {
  const response = await apiClient.request<SuccessEnvelope<RuntimeToolInvocationDetailDto>>(
    `/app/core-agent/conversations/${encodeURIComponent(conversationId)}/tool-invocations/${encodeURIComponent(invocationId)}`,
  )

  return response.data
}

export async function getCoreAgentActiveRun(conversationId: string) {
  const response = await apiClient.request<SuccessEnvelope<RuntimeActiveRunDto>>(
    `/app/core-agent/conversations/${encodeURIComponent(conversationId)}/active-run`,
  )

  return response.data
}

export async function attachCoreAgentActiveRun(
  conversationId: string,
  streamRunId: string,
  afterSequence: number | undefined,
  signal: AbortSignal,
  onEvent: (event: RuntimeSseEvent) => void,
) {
  const query = new URLSearchParams()

  if (afterSequence !== undefined) {
    query.set('after_sequence', String(afterSequence))
  }
  const queryString = query.size > 0 ? `?${query.toString()}` : ''

  return dispatchRuntimeChatStream(
    {
      afterSequence,
      eventContractVersion: afterSequence === undefined ? undefined : '2.0',
      method: 'GET',
      path: `/app/core-agent/conversations/${encodeURIComponent(conversationId)}/active-run/${encodeURIComponent(streamRunId)}/stream${queryString}`,
      signal,
      streamErrorCode: 'core_agent.stream_error',
    },
    onEvent,
  )
}

export async function deleteCoreAgentConversation(conversationId: string) {
  const response = await apiClient.request<SuccessEnvelope<{ result?: string }>>(
    `/app/core-agent/conversations/${encodeURIComponent(conversationId)}`,
    {
      method: 'DELETE',
    },
  )

  return response.data
}

export async function renameCoreAgentConversation(conversationId: string, name: string) {
  const response = await apiClient.request<
    SuccessEnvelope<{ id?: string; name?: string; result?: string }>
  >(`/app/core-agent/conversations/${encodeURIComponent(conversationId)}/name`, {
    body: JSON.stringify({
      auto_generate: false,
      name,
    }),
    method: 'POST',
  })

  return response.data
}

export async function pinCoreAgentConversation(conversation: RuntimeConversationDto) {
  const response = await apiClient.request<
    SuccessEnvelope<{ id: string; pinned: boolean; pinned_at?: string | null }>
  >(`/app/core-agent/conversations/${encodeURIComponent(conversation.id)}/pin`, {
    body: JSON.stringify(conversation),
    method: 'PATCH',
  })

  return response.data
}

export async function unpinCoreAgentConversation(conversationId: string) {
  const response = await apiClient.request<
    SuccessEnvelope<{ id: string; pinned: boolean; pinned_at?: string | null }>
  >(`/app/core-agent/conversations/${encodeURIComponent(conversationId)}/unpin`, {
    method: 'PATCH',
  })

  return response.data
}

export async function updateCoreAgentMessageFeedback({
  conversationId,
  messageId,
  rating,
}: {
  conversationId?: string
  messageId: string
  rating: ChatMessageFeedbackRating
}) {
  const response = await apiClient.request<
    SuccessEnvelope<{ id?: string; rating?: ChatMessageFeedbackRating }>
  >(`/app/core-agent/messages/${encodeURIComponent(messageId)}/feedbacks`, {
    body: JSON.stringify({
      conversation_id: conversationId,
      rating,
    }),
    method: 'POST',
  })

  return response.data
}

export async function stopCoreAgentChat(taskId: string, conversationId?: string) {
  const response = await apiClient.request<SuccessEnvelope<{ result: string }>>(
    `/app/core-agent/chat/${encodeURIComponent(taskId)}/stop`,
    {
      body: JSON.stringify({
        conversation_id: conversationId ?? undefined,
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function streamCoreAgentChat(
  options: CoreAgentSendOptions,
  onEvent: (event: RuntimeSseEvent) => void,
) {
  return dispatchRuntimeChatStream(
    {
      eventContractVersion: '2.0',
      onStreamIdentity: options.onStreamIdentity,
      path: '/app/core-agent/chat/stream',
      payload: {
        conversation_id: options.conversationId ?? undefined,
        device_timezone: getDeviceTimezone(),
        query: options.query,
        registered_model_id: options.registeredModelId ?? undefined,
        submission_id: options.submissionId,
      },
      signal: options.signal,
      streamErrorCode: 'core_agent.stream_error',
      submissionId: options.submissionId,
    },
    onEvent,
  )
}
